import { isSafeBlossomServer } from "@setu/protocol";
import { useSyncExternalStore } from "react";

/**
 * Whether this device uploads over Blossom, and the server list it uses.
 *
 * Two things live here because they answer one question — *where does the next
 * attachment go?* — and it has to be answerable **synchronously**. An upload that
 * waits on a relay fetch to find out where to send the file is an upload that lands
 * somewhere else when the fetch is slow, which is the one mistake this path cannot
 * make: the destination of a file is not something to guess on the user's behalf.
 *
 * ## Why the server list is mirrored here rather than read from the relays
 *
 * The list itself is account state — a kind-10063 (BUD-03) published under the user's
 * key — and the relays are its home. This is a *cache* of the newest one the Blossom
 * settings section has seen, written whenever that section loads or saves it. That is
 * the same shape as `sync/localSettings.ts`: local is what the app reads, the relay
 * document is the channel it arrives through.
 *
 * The cost is real and worth stating: a server list published from another client
 * after this device last opened Settings is not picked up until Settings is opened
 * again. The alternative — subscribing on the upload path — makes the destination of
 * the first upload after launch depend on whether a relay answered in time, and the
 * failure mode there is silent and unrecoverable: the file is already on a server the
 * user did not choose. A stale-but-known destination beats a raced one, and turning
 * Blossom on happens on the screen that refreshes the mirror anyway.
 *
 * ## Why not in the synced settings document
 *
 * `SyncedSettings` carries preferences that mean the same thing on every device.
 * "Upload over Blossom" does not: it depends on the servers this device has, and the
 * servers themselves already travel between devices as a signed event — the network
 * syncs the part that matters. Adding a switch there would mean two mechanisms
 * carrying halves of one decision.
 *
 * Nothing here is a secret, and nothing here is authority: every server is
 * re-validated against `assertSafeBlossomUrl` on read *and* again at upload time.
 * A tampered `localStorage` row cannot aim an upload at a host the protocol layer
 * would refuse.
 */

export type UploadProtocol = "nip96" | "blossom";

export interface BlossomPreference {
  /** Does this device upload over Blossom at all? */
  readonly enabled: boolean;
  /**
   * The account whose kind-10063 was mirrored, or `""` for none.
   *
   * Load-bearing. Media servers are an account's choice, and signing in as someone
   * else must not send their files to the previous account's servers — which is what
   * an unkeyed cache would do, silently, on the first attachment after a switch.
   */
  readonly pubkey: string;
  /** Validated server origins, in the order the account listed them. */
  readonly servers: readonly string[];
}

export const DEFAULT_BLOSSOM_PREFERENCE: BlossomPreference = {
  // Off until asked for. Turning it on moves where a user's files are stored and
  // who hosts them; that is not a default a client gets to change under someone.
  enabled: false,
  pubkey: "",
  servers: [],
};

const STORAGE_KEY = "setu-blossom";

/** `localStorage`, or undefined where it is blocked (private mode, iframes). */
function storage(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    // Reading `localStorage` *throws* on some configurations rather than being
    // absent, and an unhandled throw here would take the app down at import time.
    return undefined;
  }
}

function read(): BlossomPreference {
  const raw = storage()?.getItem(STORAGE_KEY);
  if (!raw) return DEFAULT_BLOSSOM_PREFERENCE;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_BLOSSOM_PREFERENCE;
    }
    const object = parsed as Record<string, unknown>;
    const servers = Array.isArray(object.servers)
      ? object.servers.filter(
          (value): value is string =>
            typeof value === "string" && isSafeBlossomServer(value),
        )
      : [];
    return {
      enabled: object.enabled === true,
      pubkey: typeof object.pubkey === "string" ? object.pubkey : "",
      servers,
    };
  } catch {
    // A corrupt row is not worth a broken composer: fall back to the NIP-96 path
    // and let the next visit to Settings rewrite it.
    return DEFAULT_BLOSSOM_PREFERENCE;
  }
}

let current: BlossomPreference | undefined;
const listeners = new Set<() => void>();

export function blossomPreference(): BlossomPreference {
  current ??= read();
  return current;
}

function same(a: BlossomPreference, b: BlossomPreference): boolean {
  return (
    a.enabled === b.enabled &&
    a.pubkey === b.pubkey &&
    a.servers.length === b.servers.length &&
    a.servers.every((server, index) => server === b.servers[index])
  );
}

/**
 * Change the preference, or refresh the mirrored list.
 *
 * The snapshot's identity changes only when a value does, so the settings section
 * re-mirroring an identical list on every store tick does not re-render the composer.
 */
export function setBlossomPreference(patch: Partial<BlossomPreference>): void {
  const next: BlossomPreference = { ...blossomPreference(), ...patch };
  if (same(next, blossomPreference())) return;
  current = next;
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota or a disabled store: the choice still holds for this session.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Read the preference, re-rendering when it changes. */
export function useBlossomPreference(): BlossomPreference {
  return useSyncExternalStore(subscribe, blossomPreference, blossomPreference);
}

/** Test seam: drops the in-memory copy so the next read hits storage again. */
export function resetBlossomPreferenceCache(): void {
  current = undefined;
  for (const listener of listeners) listener();
}

/** Why an upload fell back to NIP-96 despite Blossom being switched on. */
export type BlossomFallback =
  /** Blossom is on, but this account has published no usable server. */
  | "no-servers"
  /** The mirrored list belongs to a different account than the one signed in. */
  | "other-account";

export type UploadTarget =
  | {
      readonly protocol: "nip96";
      /** The host the composer names while uploading. */
      readonly host: string;
      readonly fallback?: BlossomFallback;
    }
  | {
      readonly protocol: "blossom";
      readonly host: string;
      readonly servers: readonly string[];
    };

/**
 * Where this upload is going, decided from state that is already in memory.
 *
 * Pure, and separate from the hook, because "which host did that file go to" is the
 * one question about an upload a user cannot answer afterwards — so the rule that
 * decides it is written where it can be read and tested in one place rather than
 * assembled from conditions inside a component.
 *
 * Falling back to the NIP-96 host rather than refusing is deliberate: a switched-on
 * preference with an empty list is a half-finished setup, and failing the attach
 * would be a dead end. The fallback reason comes back with the target so the UI can
 * say which protocol carried the file instead of leaving the user to assume.
 */
export function chooseUploadTarget(input: {
  readonly preference: BlossomPreference;
  readonly pubkey: string | undefined;
  /** The configured NIP-96 media host, used whenever Blossom is not in play. */
  readonly mediaHost: string;
}): UploadTarget {
  const { preference, pubkey, mediaHost } = input;
  if (!preference.enabled) return { protocol: "nip96", host: mediaHost };
  if (!pubkey || preference.pubkey !== pubkey) {
    return { protocol: "nip96", host: mediaHost, fallback: "other-account" };
  }
  const first = preference.servers[0];
  if (first === undefined) {
    return { protocol: "nip96", host: mediaHost, fallback: "no-servers" };
  }
  return { protocol: "blossom", host: first, servers: preference.servers };
}
